// Z-tickets zoals ontwerp-foto 3_z_tickets.png: links de lijst (nieuwste bovenaan), rechts de bon.
// Kassier: alleen de eigen winkel (backend kiest zelf de winkel), maand per maand bladeren.
// Beheerder: vid + van/tot meegeven.
import { useEffect, useState } from "react";
import { api, download, euro, foutTekst, vandaagISO } from "../api";
import { PdfPaginas } from "./PdfDoorlopend";

const maandLabel = (ym) => new Date(ym + "-15T12:00").toLocaleDateString("nl-BE", { month: "long", year: "numeric" });
const maandEinde = (ym) => {
  const [j, m] = ym.split("-").map(Number);
  const laatste = new Date(Date.UTC(j, m, 0)).toISOString().slice(0, 10);
  return laatste > vandaagISO() ? vandaagISO() : laatste;
};
const schuif = (ym, n) => {
  const [j, m] = ym.split("-").map(Number);
  const d = new Date(Date.UTC(j, m - 1 + n, 1));
  return d.toISOString().slice(0, 7);
};
const dagKort = (iso) => new Date(iso + "T12:00").toLocaleDateString("nl-BE", { weekday: "short", day: "numeric", month: "long", year: "numeric" });

// Drukt exact de PDF af (verborgen iframe), niet het scherm.
async function drukAf(pad) {
  const r = await api.get(pad, { responseType: "blob" });
  const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
  const f = document.createElement("iframe");
  f.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0";
  f.src = url;
  f.onload = () => { f.contentWindow.focus(); f.contentWindow.print(); setTimeout(() => { f.remove(); URL.revokeObjectURL(url); }, 60000); };
  document.body.appendChild(f);
}

export default function ZTickets({ vid, van: vanProp, tot: totProp, tarieven }) {
  const [maand, setMaand] = useState(vandaagISO().slice(0, 7));
  const van = vanProp || `${maand}-01`;
  const tot = totProp || maandEinde(maand);
  const [lijst, setLijst] = useState(null);
  const [v, setV] = useState(null);
  const [sel, setSel] = useState(null);
  const [fout, setFout] = useState("");

  useEffect(() => {
    let weg = false;
    api.get("/z-lijst", { params: { van, tot, ...(vid ? { vestiging_id: vid } : {}) } })
      .then((r) => { if (weg) return; setLijst(r.data.z); setV(r.data.vestiging); setSel(r.data.z[0] || null); setFout(""); })
      .catch((e) => { if (!weg) { setFout(foutTekst(e)); setLijst([]); setSel(null); } });
    return () => { weg = true; };
  }, [vid, van, tot]);

  const titel = vanProp ? `${van} – ${tot}` : maandLabel(maand);
  const params = { van, tot, ...(vid ? { vestiging_id: vid } : {}) };

  return (
    <div className="p-5 grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="bg-white rounded-2xl border border-slate-200 p-4">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          {!vanProp && <button className="w-8 h-8 rounded-lg bg-slate-100 text-slate-600 font-bold" title="Vorige maand" onClick={() => setMaand(schuif(maand, -1))}>‹</button>}
          <h3 className="font-bold text-slate-800 first-letter:uppercase">{v?.korte_naam} — {titel}</h3>
          {!vanProp && <button className="w-8 h-8 rounded-lg bg-slate-100 text-slate-600 font-bold disabled:opacity-30" title="Volgende maand"
            disabled={maand >= vandaagISO().slice(0, 7)} onClick={() => setMaand(schuif(maand, 1))}>›</button>}
          <button className="ml-auto bg-blue-50 text-blue-600 font-semibold rounded-xl px-3 py-2 text-sm" disabled={!lijst?.length}
            onClick={() => download("/z-zip", params, `z-tickets_${v?.id}_${van}_${tot}.zip`).catch((e) => setFout(foutTekst(e)))}>⬇ Alle Z-tickets (ZIP)</button>
        </div>
        {fout && <div className="mb-2 p-2 rounded-lg bg-red-50 text-red-700 text-sm">{fout}</div>}
        {lijst && lijst.length === 0 && <div className="text-slate-400 text-sm py-10 text-center">Geen Z-tickets in deze periode.</div>}
        <div className="max-h-[calc(100vh-260px)] overflow-auto">
          {(lijst || []).map((z) => (
            <button key={z.z_nummer} onClick={() => setSel(z)}
              className={`w-full flex items-center gap-3 p-2.5 rounded-xl border mb-2 text-left ${sel?.z_nummer === z.z_nummer ? "border-blue-600 bg-blue-50/50" : "border-slate-200"}`}>
              <span className="w-12 h-12 shrink-0 rounded-lg bg-blue-50 text-blue-600 font-extrabold text-xs flex items-center justify-center">Z-{z.z_nummer}</span>
              <span>
                <b className="text-sm first-letter:uppercase block">{dagKort(z.datum)}</b>
                <span className="block text-xs text-slate-500">Afgesloten {z.afgesloten_op.slice(11, 16)} · {z.aantal_tickets} tickets</span>
              </span>
              <b className="ml-auto">{euro(z.totaal_eur)}</b>
            </button>
          ))}
        </div>
      </div>
      <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4 flex flex-col items-center">
        {sel ? (
          <>
            {/* Letterlijk hetzelfde Z-ticket (PDF) als Boekhouding & HR ontving */}
            <div className="w-full max-w-[340px]" data-testid="z-ticket-pdf">
              <PdfPaginas key={v.id + sel.z_nummer} pad={`/z/${v.id}/${sel.z_nummer}/pdf`} onFout={(e) => setFout(foutTekst(e))} />
            </div>
            <div className="flex gap-2 mt-4">
              <button className="bg-blue-600 text-white font-semibold rounded-xl px-4 py-2.5 text-sm" data-testid="z-ticket-download"
                onClick={() => download(`/z/${v.id}/${sel.z_nummer}/pdf`, {}, `Z-${sel.z_nummer}-${v.id}-${sel.datum}.pdf`).catch((e) => setFout(foutTekst(e)))}>⬇ PDF</button>
              <button className="bg-[#1e2a44] text-white font-semibold rounded-xl px-4 py-2.5 text-sm" data-testid="z-ticket-print"
                onClick={() => drukAf(`/z/${v.id}/${sel.z_nummer}/pdf`).catch((e) => setFout(foutTekst(e)))}>🖨 Afdrukken</button>
            </div>
          </>
        ) : <div className="text-slate-400 text-sm py-16">Kies links een Z-ticket</div>}
      </div>
    </div>
  );
}
